import { useState, useMemo } from 'react';
import Swal from 'sweetalert2';
import { useBlockedUsers, type BlockedUserEntry } from '@/hooks/useBlockedUsers';
import { reincidencias } from '@/mocks/rfwReports';

function timeAgo(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffHrs = Math.floor((now.getTime() - d.getTime()) / 3600000);
  if (diffHrs < 1) return 'Ahora';
  if (diffHrs < 24) return `Hace ${diffHrs}h`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays === 1) return 'Ayer';
  return `Hace ${diffDays} días`;
}

const initialMockBlocked: BlockedUserEntry[] = reincidencias.slice(0, 4).map((r) => ({
  id: r.id,
  username: r.reportedTargetUsername,
  photoUrl: r.reportedTargetPhotoUrl,
  blockedAt: r.lastReportDate,
  reason: r.reportTypes.map((t) => {
    const map: Record<string, string> = { spam: 'Spam', insultos: 'Insultos', 'contenido-ofensivo': 'Cont. Ofensivo', 'publicidad-no-autorizada': 'Pub. No Autorizada', suplantacion: 'Suplantación' };
    return map[t] || t;
  }).join(', '),
}));

export default function AppBlockedPanel() {
  const { blockedUsers: dynamicBlocked, unblockUser } = useBlockedUsers();
  const [reportTarget, setReportTarget] = useState<string | null>(null);
  const [reportReason, setReportReason] = useState('');

  // Merge mock blocked with dynamic blocked from chat
  const allBlocked = useMemo(() => {
    const dynamicUsernames = new Set(dynamicBlocked.map((u) => u.username));
    const merged = [...dynamicBlocked];
    initialMockBlocked.forEach((mb) => {
      if (!dynamicUsernames.has(mb.username)) {
        merged.push(mb);
      }
    });
    return merged;
  }, [dynamicBlocked]);

  const reportReasons = ['Spam', 'Insultos', 'Contenido ofensivo', 'Publicidad no autorizada', 'Suplantación'];

  const handleUnblock = (username: string) => {
    Swal.fire({
      title: 'Desbloquear usuario',
      text: `¿Querés desbloquear a ${username}? Podrá interactuar con vos nuevamente.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, desbloquear',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#d1d5db',
      customClass: { popup: 'rounded-2xl', title: 'font-bold', confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap', cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap', htmlContainer: 'text-sm' },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        unblockUser(username);
        Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: `${username} desbloqueado`, showConfirmButton: false, timer: 2000, timerProgressBar: true });
      }
    });
  };

  const handleReport = (username: string) => {
    setReportTarget(username);
    setReportReason('');
  };

  const submitReport = () => {
    if (!reportReason) {
      Swal.fire({ toast: true, position: 'top-end', icon: 'warning', title: 'Seleccioná un motivo', showConfirmButton: false, timer: 1500, timerProgressBar: true });
      return;
    }
    setReportTarget(null);
    setReportReason('');
    Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: `Reporte enviado contra ${reportTarget}`, showConfirmButton: false, timer: 2000, timerProgressBar: true });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-foreground-950">Usuarios Bloqueados</h3>
        <span className="text-[10px] text-foreground-400">{allBlocked.length} usuarios</span>
      </div>

      {allBlocked.length === 0 ? (
        <div className="bg-background-50 rounded-2xl p-10 text-center border border-background-200">
          <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-shield-check-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-600 text-sm">No tenés usuarios bloqueados</p>
          <p className="text-foreground-400 text-xs mt-1">Cuando bloquees a alguien, aparecerá acá</p>
        </div>
      ) : (
        <div className="space-y-2">
          {allBlocked.map((user) => (
            <div
              key={user.id}
              className="bg-background-50 border border-rose-200 bg-rose-50/50 rounded-xl px-4 py-3.5 flex items-center gap-3 transition-all"
            >
              <div className="w-10 h-10 rounded-full overflow-hidden bg-background-100 border border-background-200 shrink-0">
                <img src={user.photoUrl} alt={user.username} className="w-full h-full object-cover object-top" loading="lazy" />
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="text-sm font-bold text-foreground-900 truncate">{user.username}</h4>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <span className="text-[10px] text-foreground-500">{user.reason}</span>
                  <span className="w-1 h-1 bg-foreground-300 rounded-full"></span>
                  <span className="text-[10px] text-foreground-400">{timeAgo(user.blockedAt)}</span>
                </div>
                <span className="inline-block mt-1 px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-600 text-[9px] font-bold">BLOQUEADO</span>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => handleUnblock(user.username)}
                  className="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-background-50 text-[10px] font-bold cursor-pointer whitespace-nowrap transition-all"
                >
                  Desbloquear
                </button>
                <button
                  onClick={() => handleReport(user.username)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-600 cursor-pointer transition-all"
                  title="Reportar"
                >
                  <i className="ri-flag-line text-sm"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Report Modal */}
      {reportTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm" onClick={() => setReportTarget(null)}></div>
          <div className="relative bg-background-50 rounded-2xl w-full max-w-sm p-5 shadow-2xl animate-fade-in-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-foreground-950">Reportar a {reportTarget}</h3>
              <button onClick={() => setReportTarget(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-500 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <p className="text-xs text-foreground-500 mb-4">Seleccioná el motivo del reporte:</p>
            <div className="space-y-2 mb-5">
              {reportReasons.map((reason) => (
                <button
                  key={reason}
                  onClick={() => setReportReason(reason)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all cursor-pointer text-left ${
                    reportReason === reason
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  <i className={`text-base ${
                    reason === 'Spam' ? 'ri-spam-2-line' :
                    reason === 'Insultos' ? 'ri-emotion-unhappy-line' :
                    reason === 'Contenido ofensivo' ? 'ri-error-warning-line' :
                    reason === 'Publicidad no autorizada' ? 'ri-advertisement-line' :
                    'ri-user-unfollow-line'
                  }`}></i>
                  {reason}
                </button>
              ))}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setReportTarget(null)}
                className="flex-1 px-4 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={submitReport}
                className="flex-1 px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                Enviar reporte
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}